/**
 * DRAFT pricing page for the Michael + Jason working sessions.
 *
 * This is NOT the live pricing page — it's mounted at /pricing-draft so we
 * can walk through a proposed four-tier, multi-year-default structure
 * without disrupting the public /pricing page.
 *
 * Structure (per 2026-04-13 discussion):
 *   - Four tiers: Core, Fair Lending, CRA, Enterprise
 *   - Commitment toggle: 1-year / 2-year / 3-year (3-year is the default
 *     and cheapest; longer commitment = better price, shorter = premium)
 *   - Annual price escalator (5% year-over-year) baked in; shown as the
 *     effective first-year monthly rate
 *   - Monthly/month-to-month is NOT offered on the page. Pilots and
 *     proofs-of-concept are handled individually.
 *
 * Base prices (3-year term, year 1):
 *   Core:         $1,995 /mo
 *   Fair Lending: $4,995 /mo
 *   CRA:          $4,995 /mo
 *   Enterprise:   $9,995 /mo
 *
 * Multipliers:
 *   3-year: 1.00 (listed price)
 *   2-year: 1.10
 *   1-year: 1.25
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';

type Term = '1year' | '2year' | '3year';

const TERM_MULTIPLIER: Record<Term, number> = {
  '3year': 1.0,
  '2year': 1.1,
  '1year': 1.25,
};

const TERM_LABELS: Record<Term, string> = {
  '3year': '3-year term',
  '2year': '2-year term',
  '1year': '1-year term',
};

interface Tier {
  id: string;
  name: string;
  tagline: string;
  basePrice: number; // 3-year monthly rate
  cta: string;
  featured?: boolean;
  modules: string[];
  featureCategories: Array<{ label: string; items: string[] }>;
  bestFor: string;
}

const TIERS: Tier[] = [
  {
    id: 'core',
    name: 'Core',
    tagline: 'Marketing compliance, HMDA validation, and regulatory intelligence.',
    basePrice: 1995,
    cta: 'Start with Core',
    bestFor: 'Banks ramping up their compliance program or replacing manual marketing review.',
    modules: ['Marketing Compliance', 'HMDA LAR Validation', 'Regulatory Intelligence', 'Exam Readiness Scoring'],
    featureCategories: [
      {
        label: 'Marketing Compliance',
        items: [
          'FDIC, NCUA, TILA, Reg Z, Reg DD, UDAAP, Equal Housing scans',
          'Ads, emails, social posts, and website copy coverage',
          'Human-in-the-loop approval workflows',
          'Examiner-ready audit trail on every check',
        ],
      },
      {
        label: 'HMDA LAR Validation',
        items: [
          '170+ CFPB edit checks (syntactical, validity, quality, macro)',
          'Plain-English AI explanations for every finding',
          'Geocoding validation and submission-ready exports',
        ],
      },
      {
        label: 'Regulatory Intelligence',
        items: [
          'Automated CFPB, OCC, FDIC enforcement scraping',
          'Priority-tagged alerts on new guidance',
          'Regulation library with team notes',
        ],
      },
      {
        label: 'Exam Readiness',
        items: [
          'Continuous readiness score',
          'Open findings & overdue item tracking',
          'Document completeness dashboard',
        ],
      },
    ],
  },
  {
    id: 'fair-lending',
    name: 'Fair Lending',
    tagline: 'Everything in Core plus the full IFLEP-aligned Fair Lending Dashboard.',
    basePrice: 4995,
    cta: 'Get Fair Lending',
    featured: true,
    bestFor: 'Banks preparing for a fair lending exam, responding to an MRA, or proactively managing disparity risk.',
    modules: ['Everything in Core', 'Fair Lending Dashboard', 'Regression Analysis', 'Redlining Analysis', 'Exam Packaging'],
    featureCategories: [
      {
        label: 'Fair Lending Dashboard (IFLEP)',
        items: [
          '20+ risk factors: U1, U2, U3, P4, P6, S4/S5/S6, R1-R4, R12, M6',
          '9 prohibited basis groups × 5 loan purposes matrix',
          'Chi-square, Fisher\'s exact, and Welch\'s t-test',
          'Wallace methodology validated, regulator-aware (FRB, FDIC, OCC)',
        ],
      },
      {
        label: 'Regression Analysis',
        items: [
          'Logistic regression for denial and steering',
          'OLS regression for pricing disparity',
          'Matched-pair analysis for examiner file review',
          'Full methodology documentation',
        ],
      },
      {
        label: 'Redlining & Marketing Demographics',
        items: [
          'R12 branch distribution analysis',
          'M6 per-AA marketing demographics matrix',
          'Cross-module link to Marketing Diversity findings',
        ],
      },
      {
        label: 'Exam Packaging',
        items: [
          'One-click examiner-ready ZIP exports',
          'Statistical findings + methodology bundled',
          'MRA remediation tracking',
        ],
      },
    ],
  },
  {
    id: 'cra',
    name: 'CRA',
    tagline: 'Everything in Core plus full CRA Lending, Service, and Investment test analytics.',
    basePrice: 4995,
    cta: 'Get CRA',
    bestFor: 'Banks with CRA exams on the horizon or $2B+ institutions needing the full three-test CRA analysis.',
    modules: ['Everything in Core', 'CRA Module', 'Assessment Area Analytics', 'Community Development Tracking', 'Gap Analysis'],
    featureCategories: [
      {
        label: 'Assessment Area Analytics',
        items: [
          'Tract-level demographics (LMI, MMCT) from FFIEC',
          'Population, lending penetration, and peer benchmarking',
          'Map-based AA visualization and geocoding',
        ],
      },
      {
        label: 'CRA Lending, Service & Investment Tests',
        items: [
          'HMDA, small business, small farm, consumer origination distribution',
          'Branch and service distribution analysis',
          'CD loan, investment, grant, and service tracking',
        ],
      },
      {
        label: 'Gap Analysis',
        items: [
          'AI-powered gap detection across lending, investment, and service',
          'Cross-module correlation with fair lending findings',
          'Targeted recommendations by AA',
        ],
      },
      {
        label: 'Exam Readiness',
        items: [
          'CRA-specific readiness score and checklist',
          'Public file document completeness tracking',
          'Board and examiner reporting',
        ],
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'The full Clarid platform with API access, partner integrations, and dedicated support.',
    basePrice: 9995,
    cta: 'Talk to Sales',
    bestFor: 'Banks running active compliance programs across every module — or any institution needing API / partner / SSO capabilities.',
    modules: [
      'Everything in Core + Fair Lending + CRA',
      'Cross-module board reports',
      'API + partner integrations (LOS, core, investment providers)',
      'SSO / SCIM / advanced RBAC',
      'Dedicated CS + priority support',
    ],
    featureCategories: [
      {
        label: 'Cross-Module Intelligence',
        items: [
          'Fair lending + marketing + CRA findings unified',
          'Board-ready reports pulling from every module',
          'Exam packaging across the full compliance stack',
        ],
      },
      {
        label: 'Integrations & API',
        items: [
          'Scoped API keys with partner identity audit',
          'LOS and core banking data feeds',
          'Channel partner integrations (Raymond James, Narmi, etc.)',
          'MCP endpoints for AI assistants (Claude, ChatGPT, Copilot)',
        ],
      },
      {
        label: 'Platform & Security',
        items: [
          'SSO (SAML/OIDC), SCIM provisioning',
          'Advanced RBAC with custom roles',
          'SOC 2 evidence package, NIST CSF 2.0 mapping',
          'AI Governance Policy and model inventory',
        ],
      },
      {
        label: 'Support',
        items: [
          'Dedicated Customer Success Manager',
          '4-hour response SLA',
          'Quarterly business reviews',
          'White-glove onboarding',
        ],
      },
    ],
  },
];

function priceFor(tier: Tier, term: Term): number {
  return Math.round(tier.basePrice * TERM_MULTIPLIER[term]);
}

function PriceCard({ tier, term }: { tier: Tier; term: Term }) {
  const price = priceFor(tier, term);
  const threeYearPrice = priceFor(tier, '3year');
  const showsPremium = term !== '3year';
  const premiumPct = term === '2year' ? '10%' : term === '1year' ? '25%' : null;

  return (
    <div
      className={`rounded-2xl border ${
        tier.featured ? 'border-ocean-600 ring-2 ring-ocean-600/10 shadow-xl' : 'border-slate-200 shadow-sm'
      } bg-white p-8 flex flex-col h-full`}
    >
      {tier.featured && (
        <div className="inline-flex self-start px-3 py-1 rounded-full bg-ocean-600 text-white text-xs font-semibold tracking-wide uppercase mb-4">
          Most popular
        </div>
      )}
      <h3 className="text-2xl font-display font-bold text-navy-900">{tier.name}</h3>
      <p className="mt-2 text-slate-600 text-sm leading-relaxed h-12">{tier.tagline}</p>

      <div className="mt-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-display font-bold text-navy-900">
            ${price.toLocaleString()}
          </span>
          <span className="text-slate-500">/mo</span>
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {TERM_LABELS[term]}, billed annually
        </div>
        {showsPremium && premiumPct && (
          <div className="mt-1 text-xs font-medium text-amber-600">
            +{premiumPct} vs 3-year (${threeYearPrice.toLocaleString()}/mo)
          </div>
        )}
        {!showsPremium && (
          <div className="mt-1 text-xs font-medium text-green-600">
            Best price — 3-year term
          </div>
        )}
      </div>

      <Link
        to="/demo"
        className={`mt-6 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
          tier.featured
            ? 'bg-ocean-600 text-white hover:bg-ocean-700'
            : 'bg-navy-900 text-white hover:bg-navy-800'
        }`}
      >
        {tier.cta}
        <ArrowRightIcon className="h-4 w-4" />
      </Link>

      <div className="mt-8 pt-6 border-t border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Best for
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">{tier.bestFor}</p>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
          Includes
        </p>
        <ul className="space-y-2">
          {tier.modules.map((m) => (
            <li key={m} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckIcon className="h-4 w-4 text-ocean-600 mt-0.5 flex-shrink-0" />
              <span>{m}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function DraftPricingPage() {
  const [term, setTerm] = useState<Term>('3year');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicNav />

      {/* Hero */}
      <section className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-800 mb-6">
            DRAFT — internal review only
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-navy-900 leading-tight">
            Built for the length of your exam cycle, not the length of a month.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
            Clarid compounds in value — year-over-year trend data, historical exam packages,
            and institutional memory across your compliance program. Our pricing is built
            for the same time horizon banks actually operate on.
          </p>

          {/* Term toggle — 3-year is default */}
          <div className="mt-10 inline-flex items-center gap-1 rounded-full bg-slate-100 p-1">
            {(['3year', '2year', '1year'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTerm(t)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  term === t ? 'bg-white shadow-sm text-navy-900' : 'text-slate-600'
                }`}
              >
                {TERM_LABELS[t]}
                {t === '3year' && (
                  <span className="ml-2 text-xs text-green-600 font-semibold">Best price</span>
                )}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            All terms billed annually. 3-year is our standard; shorter terms available at a premium.
          </p>
        </div>
      </section>

      {/* Price cards */}
      <section className="px-4 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TIERS.map((tier) => (
            <PriceCard key={tier.id} tier={tier} term={term} />
          ))}
        </div>

        <div className="mt-10 max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ocean-600 mb-2">
              Annual Escalator
            </div>
            <p className="text-slate-600 leading-relaxed">
              Multi-year contracts include a 5% annual price adjustment starting in year 2.
              Year-1 rate is what's shown above.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ocean-600 mb-2">
              Year-1 Break Clause
            </div>
            <p className="text-slate-600 leading-relaxed">
              3-year customers can exit after year 1 with 90-day notice and a 25% penalty
              on the remaining term. Built-in flexibility for a long commitment.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-ocean-600 mb-2">
              New Modules Included
            </div>
            <p className="text-slate-600 leading-relaxed">
              Modules launched during your contract term are included at no charge through
              renewal. Your rate stays locked.
            </p>
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-slate-500 max-w-3xl mx-auto">
          Every tier includes unlimited users, unlimited checks, examiner-ready audit trails,
          and our responsible-AI guarantee (human-in-the-loop on every AI-generated finding).
          Setup and onboarding included at Enterprise; $5,000 one-time at other tiers.
        </p>
      </section>

      {/* Asset-size guidance */}
      <section className="px-4 py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 text-center mb-4">
            Which tier fits your institution?
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
            A rough guide based on asset size and compliance program maturity. Every institution is different — talk to us and we'll size it with you.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                tier: 'Core',
                size: 'Under $500M',
                note: 'Smaller community banks with a single compliance officer. Focused marketing review and HMDA support.',
              },
              {
                tier: 'Fair Lending',
                size: '$500M – $2B',
                note: 'Banks with active HMDA reporting and a fair lending exam on the horizon (or behind them).',
              },
              {
                tier: 'CRA',
                size: '$2B – $10B',
                note: 'Banks with full three-test CRA exams who need AA-level analytics and CD activity tracking.',
              },
              {
                tier: 'Enterprise',
                size: 'Any size with partner needs',
                note: 'Multi-module programs, LOS/core integrations, consultant collaboration, API access, SSO.',
              },
            ].map((row) => (
              <div key={row.tier} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-ocean-600 mb-2">
                  {row.tier}
                </div>
                <div className="text-xl font-display font-bold text-navy-900 mb-3">{row.size}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{row.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detail comparison */}
      <section className="px-4 py-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 text-center mb-12">
            What's included, in detail
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            {TIERS.map((tier) => (
              <div key={tier.id} className="rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="text-xl font-display font-bold text-navy-900 mb-1">{tier.name}</h3>
                <p className="text-sm text-slate-500 mb-5">${priceFor(tier, term).toLocaleString()}/mo · {TERM_LABELS[term]}</p>
                <div className="space-y-5">
                  {tier.featureCategories.map((cat) => (
                    <div key={cat.label}>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                        {cat.label}
                      </h4>
                      <ul className="space-y-1.5">
                        {cat.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckIcon className="h-4 w-4 text-ocean-600 mt-0.5 flex-shrink-0" />
                            <span className="leading-snug">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / rationale */}
      <section className="px-4 py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-navy-900 text-center mb-10">
            Questions we're asked
          </h2>

          <div className="space-y-6">
            {[
              {
                q: 'Why is a 3-year term the default?',
                a: 'Compliance is a continuous program, not a point-in-time event. Clarid\'s value compounds — year-over-year disparity trends, historical exam packages, institutional audit trails. Community banks budget against 18–36 month exam cycles, so 3-year terms match how CCOs and CFOs plan. Multi-year also lets us invest deeper in each customer and keeps prices lower than shorter-term alternatives.',
              },
              {
                q: 'What if we want to start smaller?',
                a: '1-year and 2-year terms are available at a premium (+25% and +10% respectively vs. the 3-year rate). We also offer structured pilots for institutions that want to validate Clarid before committing — ask about our 90-day pilot program.',
              },
              {
                q: 'Can we cancel early on a 3-year contract?',
                a: 'Yes — 3-year customers can exit after year 1 with 90 days notice and a 25% penalty on the remaining contract. This protects both sides: you\'re not locked in if something fundamental changes, and we can plan the investment in your account.',
              },
              {
                q: 'What about the annual price escalator?',
                a: '5% per year starting in year 2. This is standard enterprise SaaS and covers platform investment, new module releases, and inflation. Your year-1 rate is what\'s shown on this page.',
              },
              {
                q: 'Do new modules cost extra during my contract?',
                a: 'No — modules launched during your term are included at no charge through renewal. Your rate stays locked. We\'ve added CRA, Fair Lending M6, and the Steering screens in just the past six months.',
              },
              {
                q: 'Can I start at Core and upgrade later?',
                a: 'Yes — every tier is a clean upgrade path. Start with Core, add Fair Lending or CRA when your exam calendar calls for it, or jump to Enterprise when you need API access and partner integrations. Mid-term upgrades are prorated.',
              },
              {
                q: 'What about asset-size pricing?',
                a: 'Published prices cover institutions up to roughly $5B in assets. Larger institutions, banks under active consent orders, or multi-charter holding companies need custom pricing — talk to us.',
              },
              {
                q: 'How does this compare to buying the replacement stack?',
                a: 'A typical community bank replacing the same functionality pays $45,000–$200,000/yr across Ncontracts, Wolters Kluwer fair lending, CRA Wiz, a marketing review consultant, and internal exam prep hours. Enterprise at $9,995/mo (3-year) replaces all of it — and connects the modules so findings in one surface insights in another.',
              },
            ].map((item) => (
              <details key={item.q} className="group rounded-xl border border-slate-200 bg-white p-6">
                <summary className="cursor-pointer font-semibold text-navy-900 flex items-center justify-between">
                  {item.q}
                  <span className="text-ocean-600 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
